import React from 'react';

import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Stack } from '@mui/material';

import { PricingToolSessionProvider } from './PricingToolSessionContext';
import { PricingToolTabCountProvider } from './PricingToolTabCounts';
import PricingToolToolbar from './PricingToolToolbar';
import PricingToolToolbarActions from './PricingToolToolbarActions';
import { getPricingToolTab } from './constants';
import BlackoutsTab from './tabs/blackouts/BlackoutsTab';
import EgsTab from './tabs/egs/EgsTab';
import ForecastingTab from './tabs/forecasting/ForecastingTab';
import InclusionsTab from './tabs/inclusions/InclusionsTab';
import OverridesTab from './tabs/overrides/OverridesTab';
import PackageOptionsTab from './tabs/packageOptions/PackageOptionsTab';
import PricingSummaryTab from './tabs/pricingSummary/PricingSummaryTab';
import PropertySetupTab from './tabs/propertySetup/PropertySetupTab';
import ScrapeTab from './tabs/scrape/ScrapeTab';
import SurchargesTab from './tabs/surcharges/SurchargesTab';
import type { PricingToolTab } from './types';

export default function PricingToolPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id_offer: offerId } = useParams<{ id_offer: string }>();
  const searchParams = new URLSearchParams(location.search);
  const activeTab = getPricingToolTab(searchParams.get('tab'));

  const handleTabChange = (tab: PricingToolTab) => {
    const nextSearchParams = new URLSearchParams(location.search);
    nextSearchParams.set('tab', tab);
    navigate({ pathname: location.pathname, search: nextSearchParams.toString() });
  };

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'property-setup':
        return <PropertySetupTab />;
      case 'scrape':
        return <ScrapeTab />;
      case 'package-options':
        return <PackageOptionsTab offerId={offerId} />;
      case 'inclusions':
        return <InclusionsTab offerId={offerId} />;
      case 'pricing-summary':
        return <PricingSummaryTab />;
      case 'blackouts':
        return <BlackoutsTab />;
      case 'surcharges':
        return <SurchargesTab offerId={offerId} />;
      case 'egs':
        return <EgsTab offerId={offerId} />;
      case 'overrides':
        return <OverridesTab />;
      case 'forecasting':
        return <ForecastingTab />;
      default:
        return null;
    }
  };

  return (
    <PricingToolTabCountProvider>
      <PricingToolSessionProvider offerId={offerId}>
        <Stack spacing={3}>
          <Stack spacing={3} sx={{ '@media print': { display: 'none' } }}>
            <PricingToolToolbarActions offerId={offerId} />
            <PricingToolToolbar activeTab={activeTab} onTabChange={handleTabChange} />
          </Stack>
          {renderActiveTab()}
        </Stack>
      </PricingToolSessionProvider>
    </PricingToolTabCountProvider>
  );
}
